import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Button } from '@opendate-app/setlist';
import '../styles/ftux.css';
import { EditorState } from 'draft-js';
import { richTextToHtml, richTextContentFromHtml } from '../../shared/utils/richTextHtml';
import { toastSuccess, toastError } from '../../../shared/toastHelper';
import { RevenueBaseTooltip } from '../constants/revenueBase';
import RichEditor from '../../RichEditor/components/RichEditor';
import { formatCurrencyInput, sanitizeCurrencyInput } from './simple/dealForm/dealParams';
import { MANAGED_OFFER_TEMPLATE_NOTICE } from '../constants/showManagedTemplateNotices';

const OPERATORS = [{ v: 'OR', label: 'vs' }, { v: 'AND', label: 'plus' }];
const REVENUE_BASES = [
  { v: 'default', label: 'Default' }, { v: 'gross', label: 'Gross' },
  { v: 'net_gross', label: 'Net Gross' }, { v: 'adjusted_gross', label: 'Adjusted Gross' },
];
const BONUS_OPERATORS = [{ v: 'AND', label: 'plus' }, { v: 'OR', label: 'vs' }];
const DEPOSIT_TYPES = [{ v: 'percent', label: 'Percent' }, { v: 'flat', label: 'Flat' }];
const DUE_DATE_TYPES = [{ v: 'relative', label: 'Relative' }, { v: 'specific', label: 'Specific date' }];

const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
// Coerce a possibly-incomplete currency string (e.g. "." or "12." mid-edit) to a
// clean number or null before saving — the model has no server-side validation
// for these fields, and Rails would silently cast "." to 0.0.
const numOrNull = (v) => {
  const n = num(v);
  return Number.isNaN(n) ? null : n;
};

// CONTRACT (verified against replace_and_update in Task 1, see task-1-report.md):
//  - deal_term_line_items: normal accepts_nested_attributes → send id for existing
//    rows, {id, _destroy:true} for removed rows, no id for new rows.
//  - v3_budget_bonuses / v3_budget_deposits: replace_and_update destroy_all's them
//    then recreates, and sending an existing id RAISES RecordNotFound. So resubmit
//    the FULL desired set as brand-new rows: STRIP every id, never send _destroy.
//  - v3_deal_brackets: the drawer does not edit brackets, so the payload MUST NOT
//    include v3_deal_brackets_attributes at all — its mere presence would destroy_all
//    the template's existing brackets.
// Bonus/deposit rows are recreated from scratch, so stamp the owning team_id on
// every row (hydrated rows carry it; freshly added rows would otherwise be nil).
export const buildTemplatePayload = (form, teamId) => {
  const withDestroy = (rows, removedIds) => {
    const attrs = rows.map((r) => {
      const { id, ...rest } = r;
      return id ? { id, ...rest } : { ...rest };
    });
    (removedIds || []).forEach((id) => attrs.push({ id, _destroy: true }));
    return attrs;
  };
  const freshSet = (rows) => rows.map(({ id, ...rest }) => ({ ...rest, team_id: teamId }));
  const r = form._removed || {};
  return {
    v3_offer_template: {
      name: form.name,
      dollar_amount: numOrNull(form.dollar_amount), upside_percentage: numOrNull(form.upside_percentage),
      dollar_or_percentage_operator: form.dollar_or_percentage_operator,
      from_dollar_one: form.from_dollar_one, revenue_base: form.revenue_base,
      breakeven_threshold: form.breakeven_threshold,
      artist_payout_floor: form.artist_payout_floor, artist_payout_cap: form.artist_payout_cap,
      has_merchandising: form.has_merchandising,
      soft_merch_rate: form.soft_merch_rate, soft_merch_sold_by: form.soft_merch_sold_by,
      hard_merch_rate: form.hard_merch_rate, hard_merch_sold_by: form.hard_merch_sold_by,
      has_tax_withholding: form.has_tax_withholding,
      tax_withholding_percentage: form.tax_withholding_percentage,
      tax_withholding_minimum_threshold: form.tax_withholding_minimum_threshold,
      tax_withholding_due_days: form.tax_withholding_due_days,
      guarantee_notes: form.guarantee_notes, additional_terms: form.additional_terms,
      deal_term_line_items_attributes: withDestroy(form.deal_term_line_items, r.deal_term_line_items),
      v3_budget_bonuses_attributes: freshSet(form.v3_budget_bonuses),
      v3_budget_deposits_attributes: freshSet(form.v3_budget_deposits),
    },
  };
};

const NumberField = ({ label, value, onChange, placeholder, step }) => (
  <div className="v3-edit-drawer-field">
    <label className="v3-edit-drawer-field-label">{label}</label>
    <input
      type="number"
      step={step}
      className="v3-edit-drawer-control"
      value={value === null || value === undefined ? '' : value}
      placeholder={placeholder}
      onChange={(e) => onChange(num(e.target.value))}
    />
  </div>
);
NumberField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  step: PropTypes.string,
};
NumberField.defaultProps = { value: null, placeholder: undefined, step: '0.01' };

const TextField = ({ label, value, onChange, placeholder }) => (
  <div className="v3-edit-drawer-field">
    <label className="v3-edit-drawer-field-label">{label}</label>
    <input
      type="text"
      className="v3-edit-drawer-control"
      value={value || ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);
TextField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};
TextField.defaultProps = { value: '', placeholder: undefined };

// Guarantee/Percentage inputs styled to match the Offers V3 simple editor's
// deal-terms fields: a bordered wrap with affixes. `currency` carries a "$"
// prefix + "USD" suffix and shows the value grouped to two decimals while idle
// (raw while focused for easy editing) — same behavior/helpers as the simple
// editor's MoneyField; only the CSS is drawer-local (--sl tokens, dark-safe).
const MoneyField = ({ label, value, onChange, currency, suffix }) => {
  const [focused, setFocused] = useState(false);
  const displayValue = currency && !focused ? formatCurrencyInput(value) : (value === null || value === undefined ? '' : value);
  return (
    <div className="v3-edit-drawer-field">
      <span className="v3-edit-drawer-field-label">{label}</span>
      <span className="v3-edit-drawer-affix-wrap">
        {currency ? <span className="v3-edit-drawer-affix">$</span> : null}
        <input
          aria-label={label}
          className="v3-edit-drawer-affix-input"
          type={currency ? 'text' : 'number'}
          step={currency ? undefined : '0.01'}
          inputMode="decimal"
          value={displayValue}
          onChange={(e) => onChange(currency ? sanitizeCurrencyInput(e.target.value) : num(e.target.value))}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
        {currency ? <span className="v3-edit-drawer-affix">USD</span> : null}
        {!currency && suffix ? <span className="v3-edit-drawer-affix">{suffix}</span> : null}
      </span>
    </div>
  );
};
MoneyField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  currency: PropTypes.bool,
  suffix: PropTypes.string,
};
MoneyField.defaultProps = { value: null, currency: false, suffix: null };

const SelectField = ({ label, value, onChange, options, labelSuffix }) => (
  <div className="v3-edit-drawer-field">
    <label className="v3-edit-drawer-field-label">{label}{labelSuffix}</label>
    <select className="v3-edit-drawer-control" value={value || ''} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  </div>
);
SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ v: PropTypes.string, label: PropTypes.string })).isRequired,
  labelSuffix: PropTypes.node,
};
SelectField.defaultProps = { value: '', labelSuffix: null };

const CheckboxField = ({ label, checked, onChange }) => (
  <label className="v3-edit-drawer-checkbox-row">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);
CheckboxField.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};
CheckboxField.defaultProps = { checked: false };

// HTML editor for the template's `additional_terms`, using the same RichEditor
// (draft-js) the live offer editor uses. Seeds its editorState once from `html`
// (the parent keys it by templateId so a template switch remounts and re-seeds),
// so keystrokes never reset the caret. Reports HTML up into the drawer's form,
// which the drawer's Save persists — no per-field autosave here.
const AdditionalTermsEditor = ({ html, onChange }) => {
  const [editorState, setEditorState] = useState(() => (
    html ? EditorState.createWithContent(richTextContentFromHtml(html)) : EditorState.createEmpty()
  ));
  const handleChange = (next) => {
    const contentChanged = next.getCurrentContent() !== editorState.getCurrentContent();
    setEditorState(next);
    if (contentChanged) onChange(richTextToHtml(next.getCurrentContent()));
  };
  return (
    <div className="v3-edit-drawer-richtext">
      <RichEditor wrapperClassName="border-0" editorState={editorState} onEditorStateChange={handleChange} />
    </div>
  );
};
AdditionalTermsEditor.propTypes = {
  html: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};
AdditionalTermsEditor.defaultProps = { html: '' };

const V3OfferTemplateEditDrawer = ({ team, templateId, open, onClose, onSaved, csrfToken, contextLabel }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(0);

  useEffect(() => {
    if (!open || !templateId) return undefined;
    let active = true;
    setLoading(true);
    axios.get(`/teams/${team.id}/offer_templates/v3/${templateId}`, { headers: { 'X-CSRF-TOKEN': csrfToken } })
      .then((r) => {
        if (!active) return;
        const data = r.data;
        setForm({
          ...data,
          deal_term_line_items: data.deal_term_line_items || [],
          v3_budget_bonuses: data.v3_budget_bonuses || [],
          v3_budget_deposits: data.v3_budget_deposits || [],
          _removed: { deal_term_line_items: [], v3_budget_bonuses: [], v3_budget_deposits: [] },
        });
        setUsage(data.used_by_event_template_count || 0);
      })
      .catch(() => { if (active) toastError("Couldn't load template."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, templateId, team.id, csrfToken]);

  if (!open) return null;

  const pooled = !!(form && (form.pooled || form.multi_artist));
  const managedByShowSaves = form?.managed_by_show_saves ?? false;

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const addRow = (key, blank) => setForm((f) => ({ ...f, [key]: [...f[key], blank] }));

  const changeRow = (key, idx, patch) => setForm((f) => ({
    ...f,
    [key]: f[key].map((row, i) => (i === idx ? { ...row, ...patch } : row)),
  }));

  const removeRow = (key, idx) => setForm((f) => {
    const row = f[key][idx];
    const removed = key === 'deal_term_line_items' && row && row.id
      ? { ...f._removed, [key]: [...f._removed[key], row.id] }
      : f._removed;
    return { ...f, [key]: f[key].filter((_, i) => i !== idx), _removed: removed };
  });

  const handleClose = () => {
    setForm(null);
    setUsage(0);
    onClose();
  };

  const save = () => {
    setSaving(true);
    axios.patch(`/teams/${team.id}/offer_templates/v3/${templateId}`, buildTemplatePayload(form, team.id), {
      headers: { 'X-CSRF-TOKEN': csrfToken },
    })
      .then((r) => {
        toastSuccess('Template saved.');
        onSaved(r.data);
        handleClose();
      })
      .catch((e) => {
        const msgs = e?.response?.data;
        toastError(Array.isArray(msgs) && msgs.length ? msgs[0] : 'Failed to save template.');
      })
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,13,18,.45)', display: 'flex', justifyContent: 'flex-end', zIndex: 1050 }}>
      <div className="v3-edit-drawer-scrim" onClick={handleClose} />
      <div className="v3-edit-drawer-panel">
        <div className="v3-edit-drawer-header">
          <div style={{ minWidth: 0, flex: 1 }}>
            <span className="v3-edit-drawer-eyebrow">{contextLabel}</span>
            <h2 className="v3-edit-drawer-title">{form?.name || (loading ? 'Loading…' : '—')}</h2>
          </div>
          <button type="button" className="v3-edit-drawer-close" onClick={handleClose} aria-label="Close">
            <i className="far fa-times" />
          </button>
        </div>

        {usage > 0 && !pooled && !managedByShowSaves && (
          <div className="v3-edit-drawer-usage-banner">
            This offer template is used by {usage} event template{usage === 1 ? '' : 's'}. Saving changes it for all of them.
          </div>
        )}

        <div className="v3-edit-drawer-body">
          {!form && <div className="v3-edit-drawer-empty">Loading…</div>}
          {form && pooled && (
            <div className="v3-edit-drawer-empty">
              This is a multi-artist template. Its per-artist deal terms are edited in the offer editor, not here.
            </div>
          )}
          {form && managedByShowSaves && !pooled && (
            <div className="v3-edit-drawer-empty">{MANAGED_OFFER_TEMPLATE_NOTICE}</div>
          )}
          {form && !pooled && !managedByShowSaves && (
            <>
              <div className="v3-edit-drawer-section-header v3-edit-drawer-section-header--first">
                <div className="v3-edit-drawer-section-label">Guarantee &amp; upside</div>
              </div>
              <div className="v3-edit-drawer-row-grid">
                <MoneyField label="Guarantee" value={form.dollar_amount} onChange={set('dollar_amount')} currency />
                <div className="v3-edit-drawer-field">
                  <span className="v3-edit-drawer-field-label">Operator</span>
                  <div className="v3-edit-drawer-operator-toggle">
                    {OPERATORS.map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        className={`v3-edit-drawer-operator-btn${form.dollar_or_percentage_operator === o.v ? ' v3-edit-drawer-operator-btn--active' : ''}`}
                        onClick={() => set('dollar_or_percentage_operator')(o.v)}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <MoneyField label="Percentage" value={form.upside_percentage} onChange={set('upside_percentage')} suffix="%" />
                <SelectField label="Revenue base" value={form.revenue_base} onChange={set('revenue_base')} options={REVENUE_BASES} labelSuffix={<RevenueBaseTooltip />} />
                <NumberField label="Breakeven threshold" value={form.breakeven_threshold} onChange={set('breakeven_threshold')} />
                <NumberField label="Payout floor" value={form.artist_payout_floor} onChange={set('artist_payout_floor')} />
                <NumberField label="Payout cap" value={form.artist_payout_cap} onChange={set('artist_payout_cap')} placeholder="No cap" />
              </div>
              <CheckboxField label="From dollar one" checked={form.from_dollar_one} onChange={set('from_dollar_one')} />
              <TextField label="Guarantee notes" value={form.guarantee_notes} onChange={set('guarantee_notes')} />

              <div className="v3-edit-drawer-section-header">
                <div className="v3-edit-drawer-section-label">Bonuses</div>
                <button
                  type="button"
                  className="v3-edit-drawer-add-row"
                  onClick={() => addRow('v3_budget_bonuses', {
                    id: null, ticket_count_requirement: null, dollar_or_percentage_operator: 'AND',
                    dollar_amount: null, upside_percentage: null,
                  })}
                >
                  + Add bonus
                </button>
              </div>
              {form.v3_budget_bonuses.length === 0 && <div className="v3-edit-drawer-empty">No bonuses.</div>}
              {form.v3_budget_bonuses.map((row, idx) => (
                <div key={row.id ?? `new-bonus-${idx}`} className="v3-edit-drawer-collection-row">
                  <NumberField
                    label="Ticket count"
                    step="1"
                    value={row.ticket_count_requirement}
                    onChange={(v) => changeRow('v3_budget_bonuses', idx, { ticket_count_requirement: v })}
                  />
                  <SelectField
                    label="Operator"
                    value={row.dollar_or_percentage_operator}
                    onChange={(v) => changeRow('v3_budget_bonuses', idx, { dollar_or_percentage_operator: v })}
                    options={BONUS_OPERATORS}
                  />
                  <NumberField
                    label="Amount"
                    value={row.dollar_amount}
                    onChange={(v) => changeRow('v3_budget_bonuses', idx, { dollar_amount: v })}
                  />
                  <NumberField
                    label="Upside %"
                    value={row.upside_percentage}
                    onChange={(v) => changeRow('v3_budget_bonuses', idx, { upside_percentage: v })}
                  />
                  <button type="button" className="v3-edit-drawer-remove-row" onClick={() => removeRow('v3_budget_bonuses', idx)} aria-label="Remove bonus">
                    <i className="far fa-trash-alt" />
                  </button>
                </div>
              ))}

              <div className="v3-edit-drawer-section-header">
                <div className="v3-edit-drawer-section-label">Deposits</div>
                <button
                  type="button"
                  className="v3-edit-drawer-add-row"
                  onClick={() => addRow('v3_budget_deposits', {
                    id: null, deposit_type: 'percent', value: null,
                    due_date_type: 'relative', relative_due_date: null, specific_due_date: null,
                  })}
                >
                  + Add deposit
                </button>
              </div>
              {form.v3_budget_deposits.length === 0 && <div className="v3-edit-drawer-empty">No deposits.</div>}
              {form.v3_budget_deposits.map((row, idx) => (
                <div key={row.id ?? `new-deposit-${idx}`} className="v3-edit-drawer-collection-row">
                  <SelectField
                    label="Type"
                    value={row.deposit_type}
                    onChange={(v) => changeRow('v3_budget_deposits', idx, { deposit_type: v })}
                    options={DEPOSIT_TYPES}
                  />
                  <NumberField
                    label="Value"
                    value={row.value}
                    onChange={(v) => changeRow('v3_budget_deposits', idx, { value: v })}
                  />
                  <SelectField
                    label="Due"
                    value={row.due_date_type}
                    onChange={(v) => changeRow('v3_budget_deposits', idx, { due_date_type: v })}
                    options={DUE_DATE_TYPES}
                  />
                  {row.due_date_type === 'relative' ? (
                    <NumberField
                      label="Days"
                      step="1"
                      value={row.relative_due_date}
                      onChange={(v) => changeRow('v3_budget_deposits', idx, { relative_due_date: v })}
                    />
                  ) : (
                    <div className="v3-edit-drawer-field">
                      <label className="v3-edit-drawer-field-label">Due date</label>
                      <input
                        type="date"
                        className="v3-edit-drawer-control"
                        value={row.specific_due_date || ''}
                        onChange={(e) => changeRow('v3_budget_deposits', idx, { specific_due_date: e.target.value || null })}
                      />
                    </div>
                  )}
                  <button type="button" className="v3-edit-drawer-remove-row" onClick={() => removeRow('v3_budget_deposits', idx)} aria-label="Remove deposit">
                    <i className="far fa-trash-alt" />
                  </button>
                </div>
              ))}

              <div className="v3-edit-drawer-section-header">
                <div className="v3-edit-drawer-section-label">Merchandising</div>
              </div>
              <CheckboxField label="Has merchandising" checked={form.has_merchandising} onChange={set('has_merchandising')} />
              {form.has_merchandising && (
                <div className="v3-edit-drawer-row-grid">
                  <NumberField label="Soft merch rate %" value={form.soft_merch_rate} onChange={set('soft_merch_rate')} />
                  <TextField label="Soft merch sold by" value={form.soft_merch_sold_by} onChange={set('soft_merch_sold_by')} />
                  <NumberField label="Hard merch rate %" value={form.hard_merch_rate} onChange={set('hard_merch_rate')} />
                  <TextField label="Hard merch sold by" value={form.hard_merch_sold_by} onChange={set('hard_merch_sold_by')} />
                </div>
              )}

              <div className="v3-edit-drawer-section-header">
                <div className="v3-edit-drawer-section-label">Tax withholding</div>
              </div>
              <CheckboxField label="Has tax withholding" checked={form.has_tax_withholding} onChange={set('has_tax_withholding')} />
              {form.has_tax_withholding && (
                <div className="v3-edit-drawer-row-grid">
                  <NumberField label="Withholding %" value={form.tax_withholding_percentage} onChange={set('tax_withholding_percentage')} />
                  <NumberField label="Minimum threshold" value={form.tax_withholding_minimum_threshold} onChange={set('tax_withholding_minimum_threshold')} />
                  <NumberField label="Due (days)" step="1" value={form.tax_withholding_due_days} onChange={set('tax_withholding_due_days')} />
                </div>
              )}

              <div className="v3-edit-drawer-section-header">
                <div className="v3-edit-drawer-section-label">Deal terms</div>
                <button
                  type="button"
                  className="v3-edit-drawer-add-row"
                  onClick={() => addRow('deal_term_line_items', { id: null, label: '', description: '' })}
                >
                  + Add term
                </button>
              </div>
              {form.deal_term_line_items.length === 0 && <div className="v3-edit-drawer-empty">No deal terms.</div>}
              {form.deal_term_line_items.map((row, idx) => (
                <div key={row.id ?? `new-term-${idx}`} className="v3-edit-drawer-collection-row">
                  <TextField label="Label" value={row.label} onChange={(v) => changeRow('deal_term_line_items', idx, { label: v })} />
                  <TextField label="Description" value={row.description} onChange={(v) => changeRow('deal_term_line_items', idx, { description: v })} />
                  <button type="button" className="v3-edit-drawer-remove-row" onClick={() => removeRow('deal_term_line_items', idx)} aria-label="Remove deal term">
                    <i className="far fa-trash-alt" />
                  </button>
                </div>
              ))}

              <div className="v3-edit-drawer-section-header">
                <div className="v3-edit-drawer-section-label">Additional terms</div>
              </div>
              <AdditionalTermsEditor
                key={templateId}
                html={form.additional_terms || ''}
                onChange={set('additional_terms')}
              />
            </>
          )}
        </div>

        <div className="v3-edit-drawer-footer">
          <Button variant="secondary" onClick={handleClose}>Cancel</Button>
          <Button variant="primary" disabled={saving || !form || pooled || managedByShowSaves} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

V3OfferTemplateEditDrawer.propTypes = {
  team: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired }).isRequired,
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  csrfToken: PropTypes.string,
  contextLabel: PropTypes.string,
};

V3OfferTemplateEditDrawer.defaultProps = {
  templateId: null,
  csrfToken: null,
  contextLabel: 'Offer template',
};

export default V3OfferTemplateEditDrawer;
